/**
 * MindMap
 *
 * A component for displaying interactive mind maps in chat messages
 *
 * Primary responsibilities:
 * - Visualize hierarchical relationships between concepts
 * - Allow expanding/collapsing of branches
 * - Support custom node styling and representations
 * - Adapt to AI-provided data structure with minimal constraints
 */

import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { IconBrain, IconChevronDown, IconChevronRight } from '@tabler/icons-react'

// Flexible node interface that allows for extension with custom properties
interface MindMapNode {
  id: string
  text: string
  color?: string
  icon?: string
  description?: string
  children?: MindMapNode[]
  [key: string]: any // Allow for additional custom properties
}

interface MindMapProps {
  title?: string
  rootNode: MindMapNode
  className?: string
  layout?: 'tree' | 'radial' | 'horizontal' // Support different layout options
}

export const MindMap: React.FC<MindMapProps> = ({
  title = 'Mind Map',
  rootNode,
  className,
  layout = 'tree'
}) => {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set([rootNode.id]))

  const toggleNode = (nodeId: string) => {
    const newExpandedNodes = new Set(expandedNodes)
    if (newExpandedNodes.has(nodeId)) {
      newExpandedNodes.delete(nodeId)
    } else {
      newExpandedNodes.add(nodeId)
    }
    setExpandedNodes(newExpandedNodes)
  }

  const expandAll = () => {
    const collectAllIds = (node: MindMapNode): string[] => {
      const ids = [node.id]
      if (node.children) {
        node.children.forEach((child) => {
          ids.push(...collectAllIds(child))
        })
      }
      return ids
    }

    const allIds = collectAllIds(rootNode)
    setExpandedNodes(new Set(allIds))
  }

  const collapseAll = () => {
    // Keep only the root expanded
    setExpandedNodes(new Set([rootNode.id]))
  }

  // Get node color class or use custom color if provided directly
  const getNodeColorClass = (node: MindMapNode) => {
    // If node has direct style, use that
    if (node.style) return node.style

    // If node has hex color, use that
    if (node.color?.startsWith('#')) {
      return `border-[${node.color}] bg-opacity-10 bg-[${node.color}]`
    }

    // Otherwise use predefined colors
    switch (node.color) {
      case 'blue':
        return 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
      case 'green':
        return 'border-green-400 bg-green-50 dark:bg-green-900/20'
      case 'purple':
        return 'border-purple-400 bg-purple-50 dark:bg-purple-900/20'
      case 'red':
        return 'border-red-400 bg-red-50 dark:bg-red-900/20'
      case 'yellow':
        return 'border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20'
      case 'orange':
        return 'border-orange-400 bg-orange-50 dark:bg-orange-900/20'
      case 'teal':
        return 'border-teal-400 bg-teal-50 dark:bg-teal-900/20'
      case 'indigo':
        return 'border-indigo-400 bg-indigo-50 dark:bg-indigo-900/20'
      case 'pink':
        return 'border-pink-400 bg-pink-50 dark:bg-pink-900/20'
      default:
        return 'border-gray-400 bg-gray-50 dark:bg-gray-900/20'
    }
  }

  const renderNode = (node: MindMapNode, level: number = 0) => {
    const isExpanded = expandedNodes.has(node.id)
    const hasChildren = node.children && node.children.length > 0

    return (
      <div key={node.id} className="mb-2">
        <div
          className={cn(
            'flex cursor-pointer items-start gap-2 rounded-md border-2 p-3 transition-colors',
            getNodeColorClass(node)
          )}
          onClick={() => hasChildren && toggleNode(node.id)}
        >
          {hasChildren && (
            <div className="mt-1">
              {isExpanded ? (
                <IconChevronDown size={16} className="text-gray-500" />
              ) : (
                <IconChevronRight size={16} className="text-gray-500" />
              )}
            </div>
          )}

          <div className="flex-1">
            <p className="text-sm font-medium">{node.text}</p>
            {node.description && (
              <p className="text-muted-foreground mt-1 text-xs">{node.description}</p>
            )}
          </div>
        </div>

        {hasChildren && isExpanded && (
          <div
            className={cn(
              'ml-4 mt-2 pl-8',
              layout === 'horizontal' && level === 0 ? 'flex flex-wrap gap-3' : '',
              layout === 'tree' && level === 0 ? 'grid grid-cols-1 gap-3 md:grid-cols-2' : ''
            )}
          >
            {node.children?.map((child, index) => (
              <div
                key={child.id}
                className={cn(
                  level === 0 ? '' : 'relative mt-3',
                  layout !== 'radial' && level > 0
                    ? 'border-l-2 border-dashed border-gray-300 pl-4 pt-2 dark:border-gray-700'
                    : ''
                )}
              >
                {renderNode(child, level + 1)}
                {layout === 'tree' && level === 0 && index < (node.children?.length || 0) - 1 && (
                  <div className="absolute left-full -mt-6 hidden h-2 w-8 border-b-2 border-dashed border-gray-300 md:block dark:border-gray-700"></div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div
      className={cn(
        'border-border bg-card my-4 flex flex-col overflow-hidden rounded-lg border shadow-sm transition-all',
        className
      )}
    >
      <div className="flex items-center justify-between border-b p-3">
        <div className="flex items-center gap-2">
          <IconBrain size={16} className="text-muted-foreground" />
          <h3 className="text-sm font-medium">{title}</h3>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={expandAll} className="text-xs">
            Expand All
          </Button>
          <Button variant="ghost" size="sm" onClick={collapseAll} className="text-xs">
            Collapse All
          </Button>
        </div>
      </div>

      <div className="p-4">{renderNode(rootNode)}</div>
    </div>
  )
}
